if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      static pendingForms = new Set();
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button, [type="submit"][name="add"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') || this.submitButton : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const form = evt.currentTarget;
        const submitButton = form.querySelector('.add-to-cart-button, [type="submit"][name="add"]');
        const formId = form.getAttribute('id');
        const lockKey = formId || form;
        if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true' || ProductForm.pendingForms.has(lockKey)) return;

        const agreement = form.querySelector('[id^="agree_condition-"]');
        if (agreement && !agreement.checked) {
          agreement.focus();
          return;
        }

        this.form = form;
        this.submitButton = submitButton;
        this.submitButtonText = submitButton.querySelector('span') || submitButton;
        this.error = false;
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`) : null;
        const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
        let additionConfirmed = false;
        ProductForm.pendingForms.add(lockKey);

        try {
          this.handleErrorMessage();
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
          submitButton.querySelector('.loading__spinner')?.classList.remove('hidden');
          if (stickyAddButton && stickyLoadingSpinner) {
            stickyAddButton.classList.add('loading');
            stickyLoadingSpinner.classList.remove('hidden');
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(form);
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          const properties = (productInfo || this).querySelectorAll('.product-form__input [name^="properties"]');
          properties.forEach((property) => {
            if (property.disabled || (property.form && property.form !== form) || formData.has(property.name)) return;
            if ((property.type === 'checkbox' || property.type === 'radio') && !property.checked) return;
            if (property.type === 'file') {
              if (property.files?.[0]) formData.append(property.name, property.files[0]);
              return;
            }
            if (property.value != null && property.value !== '') formData.append(property.name, property.value);
          });
          config.body = formData;

          const result = await fetch(routes.cart_add_url, config);
          const response = await result.json();
          if (!result.ok || response.status) {
            const message = typeof response.description === 'string' ? response.description : window.cartStrings?.error;
            this.handleErrorMessage(message || response.message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form-component',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }

          additionConfirmed = true;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form-component',
            productVariantId: formData.get('id'),
            cartData: response,
          });
          if (window.solasCart?.openAfterAdd(this)) return;
          if (!this.cart) {
            window.location.assign(window.routes?.cart_url || '/cart');
            return;
          }
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => this.cart?.renderContents?.(response));
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents?.(response);
          }
        } catch (error) {
          console.error(error);
          if (additionConfirmed) {
            window.location.assign(window.routes?.cart_url || '/cart');
          } else {
            this.handleErrorMessage(window.cartStrings?.error || 'تعذر تحديث السلة. حاول مرة أخرى.');
          }
        } finally {
          ProductForm.pendingForms.delete(lockKey);
          const currentForm = formId ? document.getElementById(formId) : form;
          const currentButton = currentForm?.querySelector('.add-to-cart-button, [type="submit"][name="add"]');
          new Set([submitButton, currentButton]).forEach((button) => {
            if (!button) return;
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
          if (stickyAddButton && stickyLoadingSpinner) {
            stickyAddButton.classList.remove('loading');
            stickyLoadingSpinner.classList.add('hidden');
          }
        }
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const message = document.createElement('span');
          message.className = 'product-form__error-message';
          this.errorMessageWrapper.appendChild(message);
          this.form.appendChild(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
